import React, { useCallback } from 'react';
import axios from 'axios';
import { AsyncPaginate } from 'react-select-async-paginate';

/**
 * A reusable Select component that handles server-side pagination and search.
 * 
 * @param {string} apiUrl - The API endpoint to fetch data from.
 * @param {function} mapResponse - Function to map API data to { value, label } format.
 * @param {object} params - Additional query parameters for the API.
 * @param {string} placeholder - Placeholder text for the select.
 * @param {object} rest - Other props passed to AsyncPaginate (e.g., isMulti, isClearable).
 */
const PaginatedSelect = ({
    apiUrl,
    mapResponse,
    transformResponse,
    params = {},
    placeholder = "Select...",
    ...rest
}) => {
    const loadOptions = useCallback(async (searchQuery, loadedOptions, { page }) => {
        if (!apiUrl) return { options: [], hasMore: false, additional: { page: 1 } };

        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(apiUrl, {
                params: {
                    ...params,
                    search: searchQuery,
                    page: page,
                    limit: 10
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                let data = response.data.data || [];
                
                // Allow custom transformation before mapping (e.g. deduplication)
                if (transformResponse && typeof transformResponse === 'function') {
                    data = transformResponse(data);
                }

                // Support both flat arrays and response structures with meta/pagination
                const options = data.map(mapResponse);
                
                // Determine if there are more pages
                // If the API provides pagination info, use it. Otherwise, check if we got a full page.
                const pagination = response.data.pagination || response.data.meta;
                const hasMore = pagination 
                    ? page < pagination.totalPages 
                    : data.length === 10;

                return {
                    options: options,
                    hasMore: hasMore,
                    additional: {
                        page: page + 1,
                    },
                };
            }

            return { options: [], hasMore: false, additional: { page: 1 } };
        } catch (error) {
            console.error(`Error fetching data from ${apiUrl}:`, error);
            // Handle 404 and other errors gracefully by returning an empty list
            return {
                options: [],
                hasMore: false,
                additional: { page: 1 },
            };
        }
    }, [apiUrl, params, mapResponse]);

    const customStyles = {
        control: (base) => ({
            ...base,
            minHeight: '42px',
            borderRadius: '8px',
            borderColor: '#e2e8f0',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#cbd5e1'
            }
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        ...rest.styles
    };

    return (
        <AsyncPaginate
            {...rest}
            loadOptions={loadOptions}
            additional={{ page: 1 }}
            placeholder={placeholder}
            styles={customStyles}
            debounceTimeout={300}
            menuPortalTarget={document.body}
        />
    );
};

export default PaginatedSelect;
